import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Trash2, Eye, Pencil, Shield, UserPlus } from 'lucide-react';
import api from '../../api/client';
import { Modal } from '../common/Modal';
import UserPicker from './UserPicker';
import type { FileItem, FileFolder, FilePermission, User } from '../../types';

interface ShareModalProps {
  type: 'file' | 'folder';
  item: FileItem | FileFolder;
  onClose: () => void;
  onSaved: () => void;
}

interface PermissionRow {
  user_id: number;
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
  can_share: boolean;
}

export default function ShareModal({ type, item, onClose, onSaved }: ShareModalProps) {
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [permissions, setPermissions] = useState<Record<number, PermissionRow>>({});
  const [existing, setExisting] = useState<FilePermission[]>(item.permissions ?? []);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValues, setEditingValues] = useState<Partial<FilePermission>>({});
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/api/v1/users/search?search=&per_page=1000');
        setUsers(res.data?.data ?? []);
      } catch {
        // ignore
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    setExisting(item.permissions ?? []);
    setEditingId(null);
    setEditingValues({});
  }, [item.permissions]);

  const usersById = useMemo(() => {
    const map = new Map<number, User>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const handlePermissionChange = (userId: number, key: keyof PermissionRow, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] ?? {
          user_id: userId,
          can_read: true,
          can_write: false,
          can_delete: false,
          can_share: false,
        }),
        [key]: value,
      },
    }));
  };

  const handleAddUsers = async () => {
    setSaving(true);
    setError(null);

    try {
      const endpoint =
        type === 'file'
          ? `/api/v1/files/${item.id}/share`
          : `/api/v1/file-folders/${item.id}/share`;

      for (const userId of selectedUserIds) {
        const row = permissions[userId] ?? {
          user_id: userId,
          can_read: true,
          can_write: false,
          can_delete: false,
          can_share: false,
        };

        await api.post(endpoint, row);
      }

      setSelectedUserIds([]);
      setPermissions({});
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Paylaşım kaydedilirken bir hata oluştu.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (permissionId: number) => {
    setRemovingId(permissionId);
    setError(null);

    try {
      await api.delete(`/api/v1/file-permissions/${permissionId}`);
      setExisting((prev) => prev.filter((p) => p.id !== permissionId));
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'İzin kaldırılırken bir hata oluştu.';
      setError(msg);
    } finally {
      setRemovingId(null);
    }
  };

  const handleUpdate = async (perm: FilePermission) => {
    setUpdatingId(perm.id);
    setError(null);

    try {
      await api.put(`/api/v1/file-permissions/${perm.id}`, {
        can_read: true,
        can_write: editingValues.can_write ?? perm.can_write,
      });

      setEditingId(null);
      setEditingValues({});
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'İzin güncellenirken bir hata oluştu.';
      setError(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const itemName = 'original_name' in item ? item.original_name : item.name;

  return (
    <Modal
      open
      onClose={onClose}
      align="start"
      overlayClassName="overflow-y-auto bg-black/50 p-4 py-4"
      panelUnstyled
      panelClassName="w-full max-w-none rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-lg sm:rounded-xl sm:p-6"
      panelTestId="share-modal"
      labelledBy="share-modal-title"
    >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 id="share-modal-title" className="text-lg font-bold text-slate-900">Paylaş</h2>
            <p className="text-sm text-slate-500">{itemName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        {/* Mevcut izinler */}
        {existing.length > 0 && (
          <div className="mb-6 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Mevcut Erişimler
            </h3>
            <div className="rounded-lg border border-slate-200 bg-slate-50">
              {existing.map((perm) => {
                const u = perm.user ?? usersById.get(perm.user_id);
                const isEditing = editingId === perm.id;
                const currentWrite = editingValues.can_write ?? perm.can_write;

                return (
                  <div
                    key={perm.id}
                    data-testid={`permission-row-${perm.id}`}
                    className="border-b border-slate-200 px-3 py-2 last:border-b-0"
                  >
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                          {u ? u.name[0] : '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">
                            {u ? `${u.name} ${u.surname}` : `Kullanıcı #${perm.user_id}`}
                          </p>
                          {!isEditing && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {perm.can_read && (
                                <span className="flex items-center gap-0.5">
                                  <Eye className="h-3 w-3" /> Görüntüle
                                </span>
                              )}
                              {perm.can_write && (
                                <span className="flex items-center gap-0.5">
                                  <Pencil className="h-3 w-3" /> Düzenle
                                </span>
                              )}
                              {perm.can_share && (
                                <span className="flex items-center gap-0.5">
                                  <Shield className="h-3 w-3" /> Paylaş
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleUpdate(perm)}
                              disabled={updatingId === perm.id || currentWrite === perm.can_write}
                              data-testid={`update-permission-${perm.id}`}
                              className="rounded p-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                            >
                              {updatingId === perm.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Kaydet'
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setEditingValues({});
                              }}
                              disabled={updatingId === perm.id}
                              className="rounded p-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                            >
                              İptal
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingId(perm.id);
                                setEditingValues({ can_write: perm.can_write });
                              }}
                              data-testid={`edit-permission-${perm.id}`}
                              aria-label="İzni düzenle"
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemove(perm.id)}
                              disabled={removingId === perm.id}
                              data-testid={`remove-permission-${perm.id}`}
                              className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            >
                              {removingId === perm.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div data-testid="permission-edit-controls" className="mt-2 flex flex-wrap items-center gap-4 pl-9">
                        <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked
                            disabled
                            className="h-4 w-4 rounded border-slate-300 text-blue-600"
                          />
                          Görüntüle
                        </label>
                        <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={currentWrite}
                            onChange={(e) =>
                              setEditingValues((prev) => ({ ...prev, can_write: e.target.checked }))
                            }
                            data-testid="edit-write-permission-checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600"
                          />
                          Düzenle
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Yeni kullanıcı ekle */}
        <div className="space-y-3">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <UserPlus className="h-3.5 w-3.5" />
            Kullanıcı Ekle
          </h3>

          <UserPicker
            selectedIds={selectedUserIds}
            onChange={setSelectedUserIds}
            excludeIds={existing.map((p) => p.user_id)}
          />

          {selectedUserIds.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-xs font-medium text-slate-600">İzinler</p>
              <div className="space-y-2">
                {selectedUserIds.map((userId) => {
                  const u = usersById.get(userId);
                  const row = permissions[userId] ?? {
                    can_read: true,
                    can_write: false,
                    can_delete: false,
                    can_share: false,
                  };
                  return (
                    <div key={userId} className="flex flex-col items-start gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="font-medium text-slate-700">
                        {u ? `${u.name} ${u.surname}` : `Kullanıcı #${userId}`}
                      </span>
                      <div className="flex items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={row.can_read}
                            disabled
                            className="h-4 w-4 rounded border-slate-300 text-blue-600"
                          />
                          Görüntüle
                        </label>
                        <label className="flex cursor-pointer items-center gap-1 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={row.can_write}
                            onChange={(e) =>
                              handlePermissionChange(userId, 'can_write', e.target.checked)
                            }
                            data-testid="share-write-permission-checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600"
                          />
                          Düzenle
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Kapat
          </button>
          <button
            onClick={handleAddUsers}
            disabled={saving || selectedUserIds.length === 0}
            data-testid="save-share-button"
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Paylaş
          </button>
        </div>
    </Modal>
  );
}
